import React, { useState, useEffect, useMemo } from 'react';
import { itemsAPI, inventoryAPI, warehousesAPI } from '@/lib/api';
import { Package, Plus, Minus, Save, Edit2, CheckCircle, Search, Filter, History, Calendar, TrendingUp, TrendingDown, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';

const BodegaInventoryPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [movementType, setMovementType] = useState('set');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('Conteo físico');
  const [notes, setNotes] = useState('');
  const [inventoryData, setInventoryData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [showInBoxes, setShowInBoxes] = useState(false);
  
  // Obtener el warehouse_id del usuario bodega
  const userWarehouseId = user?.warehouse_id;
  
  // Función para obtener el stock de un item para la bodega del usuario
  const getItemStock = (item) => {
    if (!userWarehouseId) return item.current_stock || 0;
    // Si el item tiene stock_by_warehouse, usar el stock de la bodega del usuario
    if (item.stock_by_warehouse && item.stock_by_warehouse[userWarehouseId] !== undefined) {
      return item.stock_by_warehouse[userWarehouseId];
    }
    // Si no hay stock específico para esta bodega, retornar 0
    return 0;
  };
  
  // Estados para historial
  const [activeTab, setActiveTab] = useState('inventory');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [historyWarehouse, setHistoryWarehouse] = useState('all');
  const [historyType, setHistoryType] = useState('all');
  const [expandedHistoryItem, setExpandedHistoryItem] = useState(null);

  useEffect(() => {
    loadItems();
    loadWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, historyWarehouse, historyType]);

  const loadWarehouses = async () => {
    try {
      // Usar getActive para usuarios bodega (getAll requiere admin)
      const response = await warehousesAPI.getActive();
      setWarehouses(response.data);
    } catch (error) {
      console.error('Error loading warehouses:', error);
    }
  };
  
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = { limit: 100 };
      if (historyWarehouse !== 'all') params.warehouseId = historyWarehouse;
      if (historyType !== 'all') params.movementType = historyType;
      
      const response = await inventoryAPI.getHistory(params);
      setHistory(response.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadItems = async () => {
    try {
      const response = await itemsAPI.getAll(false);
      setItems(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  // Extraer proveedores únicos
  const suppliers = useMemo(() => {
    const uniqueSuppliers = [...new Set(items.map(item => item.supplier || 'Sin proveedor'))];
    return uniqueSuppliers.sort();
  }, [items]);

  // Filtrar items y agregar stock de bodega
  const filteredItems = useMemo(() => {
    return items
      .map(item => ({
        ...item,
        // Agregar el stock específico de la bodega del usuario
        bodega_stock: getItemStock(item)
      }))
      .filter(item => {
        const matchesSearch = !searchQuery || 
          item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.code.toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesSupplier = selectedSupplier === 'all' || 
          (item.supplier || 'Sin proveedor') === selectedSupplier;
        
        return matchesSearch && matchesSupplier;
      });
  }, [items, searchQuery, selectedSupplier, userWarehouseId]);

  const openAdjustDialog = (item) => {
    setSelectedItem(item);
    setMovementType('set');
    setQuantity(item.bodega_stock.toString());
    setReason('Conteo físico');
    setNotes('');
    setAdjustDialogOpen(true);
  };

  const handleAdjustStock = async (e) => {
    e.preventDefault();
    if (!quantity || parseFloat(quantity) < 0) {
      toast.error('Ingresa una cantidad válida');
      return;
    }

    try {
      await inventoryAPI.adjust(selectedItem.id, {
        movement_type: movementType,
        quantity: parseFloat(quantity),
        reason,
        notes,
        warehouse_id: userWarehouseId // Siempre pasar el warehouse_id
      });
      toast.success('Stock actualizado');
      setAdjustDialogOpen(false);
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ajustar stock');
    }
  };

  const getStockStatus = (item) => {
    const stock = item.bodega_stock ?? item.current_stock ?? 0;
    if (stock < item.minimum_stock) {
      return { color: 'border-red-400 bg-red-50', badge: 'bg-red-500', label: 'Bajo' };
    } else if (stock < item.minimum_stock * 1.5) {
      return { color: 'border-amber-400 bg-amber-50', badge: 'bg-amber-500', label: 'Medio' };
    }
    return { color: 'border-green-400 bg-green-50', badge: 'bg-green-500', label: 'OK' };
  };

  const handleQuickUpdate = (itemId, value) => {
    setInventoryData(prev => ({
      ...prev,
      [itemId]: value
    }));
  };

  const saveQuickUpdate = async (item) => {
    const newValue = inventoryData[item.id];
    if (newValue === undefined || newValue === '') return;

    try {
      await inventoryAPI.adjust(item.id, {
        movement_type: 'set',
        quantity: parseFloat(newValue),
        reason: 'Conteo físico',
        notes: 'Actualización rápida',
        warehouse_id: userWarehouseId // Siempre pasar el warehouse_id
      });
      toast.success(`${item.name} actualizado`);
      setInventoryData(prev => {
        const { [item.id]: _, ...rest } = prev;
        return rest;
      });
      loadItems();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const changesCount = Object.keys(inventoryData).filter(k => inventoryData[k] !== '').length;

  const saveAllChanges = async () => {
    const updates = Object.entries(inventoryData).filter(([_, value]) => value !== '');
    
    if (updates.length === 0) {
      toast.error('No hay cambios para guardar');
      return;
    }

    let successCount = 0;
    for (const [itemId, newStock] of updates) {
      try {
        await inventoryAPI.adjust(itemId, {
          movement_type: 'set',
          quantity: parseFloat(newStock),
          reason: 'Conteo físico',
          notes: 'Actualización masiva',
          warehouse_id: userWarehouseId // Siempre pasar el warehouse_id
        });
        successCount++;
      } catch (error) {
        console.error('Error updating item:', itemId);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} productos actualizados`);
      setInventoryData({});
      loadItems();
    }
  };

  // Función para formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  // Tipo de movimiento a etiqueta
  const getMovementBadge = (type) => {
    const badges = {
      reception: { label: 'Recepción', color: 'bg-green-100 text-green-700' },
      adjustment: { label: 'Ajuste', color: 'bg-blue-100 text-blue-700' },
      dispatch: { label: 'Despacho', color: 'bg-orange-100 text-orange-700' },
      set: { label: 'Conteo', color: 'bg-purple-100 text-purple-700' },
      add: { label: 'Entrada', color: 'bg-emerald-100 text-emerald-700' },
      subtract: { label: 'Salida', color: 'bg-red-100 text-red-700' },
    };
    return badges[type] || { label: type, color: 'bg-slate-100 text-slate-700' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="pb-32" data-testid="bodega-inventory-page">
      {/* Header compacto para móvil */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 -mx-4 px-4 py-3 mb-4">
        <h1 className="text-xl font-bold text-slate-900">Inventario</h1>
        <p className="text-sm text-muted-foreground">Actualiza el stock de bodega</p>
      </div>

      {/* Tabs para cambiar entre Inventario e Historial */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="inventory" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Inventario
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Historial
          </TabsTrigger>
        </TabsList>

        {/* Tab de Inventario */}
        <TabsContent value="inventory" className="mt-4">
      {/* Búsqueda y filtros - diseño móvil */}
      <div className="space-y-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar producto..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 text-base"
            data-testid="search-input"
          />
        </div>
        
        <div className="flex gap-2">
          <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
            <SelectTrigger className="h-12 flex-1">
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Filtrar por proveedor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los proveedores</SelectItem>
              {suppliers.map(sup => (
                <SelectItem key={sup} value={sup}>{sup}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant={showInBoxes ? "default" : "outline"}
            className="h-12 px-3"
            onClick={() => setShowInBoxes(!showInBoxes)}
            data-testid="toggle-boxes-view"
          >
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline ml-2">{showInBoxes ? 'Unidades' : 'Cajas'}</span>
          </Button>
        </div>
      </div>

      {/* Info de cambios pendientes */}
      {changesCount > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800 font-medium">
            {changesCount} producto(s) con cambios pendientes
          </p>
        </div>
      )}

      {/* Lista de productos - diseño tipo tarjeta para móvil */}
      <div className="space-y-3">
        {filteredItems.map((item, index) => {
          const status = getStockStatus(item);
          const hasChange = inventoryData[item.id] !== undefined && inventoryData[item.id] !== '';
          
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.02 }}
              className={`bg-white rounded-xl border-2 ${hasChange ? 'border-blue-400 bg-blue-50/30' : status.color} p-4 shadow-sm`}
              data-testid={`inventory-item-${item.code}`}
            >
              {/* Encabezado del producto */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-900 truncate">{item.name}</h3>
                    <span className={`${status.badge} text-white text-xs px-2 py-0.5 rounded-full`}>
                      {status.label}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {item.code} • <span className="text-blue-600 font-medium">{item.supplier || 'Sin proveedor'}</span>
                  </p>
                </div>
              </div>

              {/* Stock actual y controles */}
              <div className="flex items-center justify-between gap-3">
                {/* Stock actual */}
                <div className="flex-shrink-0">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">
                    Stock actual {showInBoxes && item.unidades_por_caja ? '(cajas)' : ''}
                  </p>
                  {showInBoxes && item.unidades_por_caja ? (
                    <>
                      <p className="text-2xl font-bold text-slate-900">
                        {(item.bodega_stock / item.unidades_por_caja).toFixed(1)}
                        <span className="text-sm font-normal text-slate-500 ml-1">cajas</span>
                      </p>
                      <p className="text-xs text-slate-400">({item.bodega_stock} {item.unit})</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-slate-900">
                        {item.bodega_stock}
                        <span className="text-sm font-normal text-slate-500 ml-1">{item.unit}</span>
                      </p>
                      <p className="text-xs text-slate-400">Mín: {item.minimum_stock}</p>
                    </>
                  )}
                </div>

                {/* Input de nuevo stock */}
                <div className="flex-1 max-w-[140px]">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1 text-center">Nuevo stock</p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 flex-shrink-0"
                      onClick={() => {
                        const current = parseFloat(inventoryData[item.id] ?? item.bodega_stock);
                        handleQuickUpdate(item.id, Math.max(0, current - 1).toString());
                      }}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.1"
                      value={inventoryData[item.id] ?? ''}
                      placeholder={item.bodega_stock.toString()}
                      onChange={(e) => handleQuickUpdate(item.id, e.target.value)}
                      className="h-10 text-center font-bold text-lg px-1"
                      data-testid={`input-${item.code}`}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 flex-shrink-0"
                      onClick={() => {
                        const current = parseFloat(inventoryData[item.id] ?? item.bodega_stock);
                        handleQuickUpdate(item.id, (current + 1).toString());
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Botón de guardar individual (si hay cambio) */}
              {hasChange && (
                <Button
                  onClick={() => saveQuickUpdate(item)}
                  className="w-full mt-3 bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Guardar cambio
                </Button>
              )}

              {/* Botón para ajuste detallado */}
              {!hasChange && (
                <Button
                  variant="ghost"
                  onClick={() => openAdjustDialog(item)}
                  className="w-full mt-3 text-slate-600"
                  size="sm"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Ajuste detallado
                </Button>
              )}
            </motion.div>
          );
        })}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No se encontraron productos</p>
        </div>
      )}
        </TabsContent>

        {/* Tab de Historial */}
        <TabsContent value="history" className="mt-4">
          {/* Filtros del historial */}
          <div className="space-y-3 mb-4">
            <div className="flex gap-2">
              <Select value={historyWarehouse} onValueChange={setHistoryWarehouse}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Todas las bodegas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las bodegas</SelectItem>
                  {warehouses.map(wh => (
                    <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={historyType} onValueChange={setHistoryType}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Todos los tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  <SelectItem value="reception">Recepciones</SelectItem>
                  <SelectItem value="adjustment">Ajustes</SelectItem>
                  <SelectItem value="dispatch">Despachos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button 
              variant="outline" 
              onClick={loadHistory} 
              className="w-full"
              disabled={historyLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${historyLoading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>

          {/* Lista del historial */}
          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No hay movimientos registrados</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((mov, index) => {
                const badge = getMovementBadge(mov.type);
                const isExpanded = expandedHistoryItem === mov.id;
                
                return (
                  <motion.div
                    key={mov.id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm"
                    data-testid={`history-item-${index}`}
                  >
                    {/* Encabezado del movimiento */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${badge.color}`}>
                            {badge.label}
                          </span>
                          {mov.warehouse_name && (
                            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                              {mov.warehouse_name}
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-slate-900 mt-1">
                          {mov.item_name || 'Múltiples productos'}
                        </p>
                        {mov.item_code && (
                          <p className="text-xs text-slate-500">{mov.item_code}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${
                          mov.quantity_change > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {mov.quantity_change > 0 ? '+' : ''}{mov.quantity_change}
                        </p>
                        {mov.new_stock !== null && (
                          <p className="text-xs text-slate-500">
                            Stock: {mov.new_stock}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Fecha y usuario */}
                    <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(mov.date)}
                      </span>
                      {mov.created_by_name && (
                        <span>Por: {mov.created_by_name}</span>
                      )}
                    </div>

                    {/* Detalles expandibles para recepciones */}
                    {mov.type === 'reception' && mov.items && mov.items.length > 0 && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedHistoryItem(isExpanded ? null : mov.id)}
                          className="w-full mt-2 text-slate-600"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="w-4 h-4 mr-1" />
                              Ocultar detalles
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4 mr-1" />
                              Ver {mov.items.length} productos
                            </>
                          )}
                        </Button>
                        
                        {isExpanded && (
                          <div className="mt-3 space-y-2 bg-slate-50 rounded-lg p-3">
                            {mov.items.map((item, idx) => (
                              <div key={idx} className="flex justify-between items-center text-sm">
                                <span className="text-slate-700">{item.item_name}</span>
                                <span className="font-medium text-green-600">+{item.quantity}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {/* Notas */}
                    {mov.notes && (
                      <p className="text-xs text-slate-500 mt-2 italic">
                        "{mov.notes}"
                      </p>
                    )}

                    {/* Referencia (factura, etc.) */}
                    {mov.reference_number && (
                      <p className="text-xs text-slate-500 mt-1">
                        Ref: {mov.reference_number}
                        {mov.supplier && ` • ${mov.supplier}`}
                      </p>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Botón flotante para guardar todos los cambios */}
      {changesCount > 0 && (
        <div className="fixed bottom-20 left-0 right-0 px-4 z-40">
          <Button
            onClick={saveAllChanges}
            className="w-full h-14 text-lg font-bold bg-primary hover:bg-primary/90 shadow-xl rounded-xl"
            data-testid="save-all-button"
          >
            <CheckCircle className="w-6 h-6 mr-2" />
            Guardar {changesCount} cambio(s)
          </Button>
        </div>
      )}

      {/* Dialog para ajuste detallado */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent className="max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">Ajustar Stock</DialogTitle>
            <p className="text-sm text-muted-foreground">{selectedItem?.name}</p>
          </DialogHeader>
          <form onSubmit={handleAdjustStock}>
            <div className="space-y-4 py-2">
              <div className="bg-slate-100 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500 uppercase">Stock Actual</p>
                <p className="text-3xl font-bold text-slate-900">
                  {selectedItem?.bodega_stock ?? selectedItem?.current_stock}
                  <span className="text-base font-normal text-slate-500 ml-1">{selectedItem?.unit}</span>
                </p>
              </div>

              <div>
                <Label>Tipo de Movimiento</Label>
                <Select value={movementType} onValueChange={setMovementType}>
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="set">Establecer cantidad</SelectItem>
                    <SelectItem value="add">Agregar (entrada)</SelectItem>
                    <SelectItem value="subtract">Restar (salida)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Cantidad</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="h-12 text-lg font-bold text-center"
                  required
                />
              </div>

              <div>
                <Label>Motivo</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Conteo físico">Conteo físico</SelectItem>
                    <SelectItem value="Recepción de proveedor">Recepción de proveedor</SelectItem>
                    <SelectItem value="Despacho a locales">Despacho a locales</SelectItem>
                    <SelectItem value="Merma">Merma</SelectItem>
                    <SelectItem value="Ajuste de inventario">Ajuste de inventario</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Observaciones (opcional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Detalles adicionales..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setAdjustDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90">
                <Save className="w-4 h-4 mr-2" />
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BodegaInventoryPage;
